/**
 * Routes, and the auth gate in front of them.
 *
 * Author: Danish Khan
 */
import type { ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import { Shell } from './components/Shell.tsx';
import { useSession } from './lib/session.tsx';
import { LoginPage } from './pages/Login.tsx';
import { HomePage } from './pages/Home.tsx';
import { InboxPage } from './pages/Inbox.tsx';
import { HandoffsPage } from './pages/Handoffs.tsx';
import { PipelinePage } from './pages/Pipeline.tsx';
import { ContactsPage } from './pages/Contacts.tsx';
import { CataloguePage } from './pages/Catalogue.tsx';
import { HealthPage } from './pages/Health.tsx';
import { PeoplePage } from './pages/People.tsx';
import { ConfigStudioPage } from './pages/ConfigStudio.tsx';
import { CampaignsPage } from './pages/Campaigns.tsx';
import { QualityPage } from './pages/Quality.tsx';
import { AnalyticsPage } from './pages/Analytics.tsx';
import { OnboardingPage } from './pages/Onboarding.tsx';

function Gate({ children }: { children: ReactNode }): ReactNode {
  const { session, loading } = useSession();
  const location = useLocation();
  if (loading) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-fg-muted" role="status">
        Loading your session…
      </div>
    );
  }
  if (!session) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return <Shell>{children}</Shell>;
}

export function AppRoutes(): ReactNode {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/"
        element={
          <Gate>
            <HomePage />
          </Gate>
        }
      />
      <Route
        path="/inbox"
        element={
          <Gate>
            <InboxPage />
          </Gate>
        }
      />
      <Route
        path="/inbox/:conversationId"
        element={
          <Gate>
            <InboxPage />
          </Gate>
        }
      />
      <Route
        path="/handoffs"
        element={
          <Gate>
            <HandoffsPage />
          </Gate>
        }
      />
      <Route
        path="/pipeline"
        element={
          <Gate>
            <PipelinePage />
          </Gate>
        }
      />
      <Route
        path="/contacts"
        element={
          <Gate>
            <ContactsPage />
          </Gate>
        }
      />
      <Route
        path="/catalogue"
        element={
          <Gate>
            <CataloguePage />
          </Gate>
        }
      />
      <Route
        path="/catalogue/:productId"
        element={
          <Gate>
            <CataloguePage />
          </Gate>
        }
      />
      <Route
        path="/system"
        element={
          <Gate>
            <HealthPage />
          </Gate>
        }
      />
      <Route
        path="/people"
        element={
          <Gate>
            <PeoplePage />
          </Gate>
        }
      />
      <Route
        path="/campaigns"
        element={
          <Gate>
            <CampaignsPage />
          </Gate>
        }
      />
      <Route
        path="/studio"
        element={
          <Gate>
            <ConfigStudioPage />
          </Gate>
        }
      />
      <Route
        path="/quality"
        element={
          <Gate>
            <QualityPage />
          </Gate>
        }
      />
      <Route
        path="/analytics"
        element={
          <Gate>
            <AnalyticsPage />
          </Gate>
        }
      />
      <Route
        path="/onboarding"
        element={
          <Gate>
            <OnboardingPage />
          </Gate>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
